import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Booking } from '../../api/rooms';
import {
  GRID_HEIGHT,
  dayNumberLabel,
  instantFor,
  minutesToY,
  minutesOfInstant,
  packLanes,
  timeLabel,
  todayInBuilding,
  weekDays,
  weekdayLabel,
} from '../../lib/calendar';
import { NowLine } from './NowLine';
import { TimeGrid } from './TimeGrid';
import { useDragToCreate } from './useDragToCreate';

export interface WeekGridProps {
  date: string;
  bookings: Booking[];
  onOpen: (booking: Booking) => void;
  /** Drag finished — the room is chosen in the dialog (REQ-037). */
  onDraft: (draft: { date: string; fromMinute: number; toMinute: number }) => void;
}

/**
 * The week view: seven day columns, overlapping meetings packed into lanes.
 *
 * A drag here fixes the date and time but not the room — the FRS is explicit
 * that the week grid pre-fills those two and leaves the room editable in the
 * dialog, because a week column is a day, not a room.
 */
export function WeekGrid({ date, bookings, onOpen, onDraft }: WeekGridProps): ReactElement {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const today = todayInBuilding();

  const days = useMemo(() => weekDays(date), [date]);

  const { selection, handlers } = useDragToCreate((s) =>
    onDraft({ date: s.key, fromMinute: s.fromMinute, toMinute: s.toMinute }),
  );

  const byDate = useMemo(() => {
    const map = new Map<string, Booking[]>();
    for (const booking of bookings) {
      const list = map.get(booking.date) ?? [];
      list.push(booking);
      map.set(booking.date, list);
    }
    return map;
  }, [bookings]);

  return (
    <TimeGrid
      className="cal-week"
      heads={days.map((day) => (
        <div key={day} className={day === today ? 'cal-day-head today' : 'cal-day-head'}>
          {weekdayLabel(day, locale)}
          <span>{dayNumberLabel(day, locale)}</span>
        </div>
      ))}
      columns={days.map((day) => {
        const placed = packLanes(
          byDate.get(day) ?? [],
          (b) => minutesOfInstant(b.starts_at),
          (b) => minutesOfInstant(b.ends_at),
        );

        return (
          <div
            key={day}
            className="cal-room-col cal-day-col"
            style={{ height: GRID_HEIGHT, touchAction: 'none' }}
            {...handlers(day)}
          >
            {placed.map(({ item, lane, lanes }) => {
              const top = minutesToY(minutesOfInstant(item.starts_at));
              const height = Math.max(18, minutesToY(minutesOfInstant(item.ends_at)) - top);
              const width = 100 / lanes;

              return (
                <button
                  key={item.id}
                  type="button"
                  className="cal-event"
                  title={`${item.title} — ${item.room_name}`}
                  onClick={() => onOpen(item)}
                  style={{
                    top,
                    height,
                    left: `calc(${lane * width}% + 2px)`,
                    width: `calc(${width}% - 4px)`,
                    insetInlineEnd: 'auto',
                  }}
                >
                  <b>{item.title}</b>
                  {item.room_name}
                </button>
              );
            })}

            <NowLine day={day} />

            {selection?.key === day ? (
              <div
                className="cal-drag"
                style={{
                  top: minutesToY(selection.fromMinute),
                  height: Math.max(
                    20,
                    minutesToY(selection.toMinute) - minutesToY(selection.fromMinute),
                  ),
                }}
              >
                {timeLabel(instantFor(day, selection.fromMinute), locale)} –{' '}
                {timeLabel(instantFor(day, selection.toMinute), locale)}
              </div>
            ) : null}
          </div>
        );
      })}
    />
  );
}
